import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import ApiLoader from "../common/loader";
import { routes } from "../routings";
import { REACT_APP_API_KEY, REACT_APP_API_URL } from "../common/config";
import { getOfficerName, getOfficerToken, officerAuthHeader, officerLogout } from "../common/officerAuth";
import OfficerLoginForm from "./officerLoginForm";

const BootInstall = () => {
  const { token } = useParams();
  const history = useHistory();

  // Keep a valid sign-in. The queue link is often opened by the filer, so show
  // who is signed in and let them log out before this install is credited.
  const [authed, setAuthed] = useState(!!getOfficerToken());
  const [officerName, setOfficerName] = useState(getOfficerName() || "");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [files, setFiles] = useState([]);
  const submitCancelRef = useRef(null);

  const maxFiles = 5;

  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !submitting &&
      !error &&
      data &&
      !data.already_booted &&
      confirmationChecked &&
      files.length >= 1 &&
      files.length <= maxFiles
    );
  }, [loading, submitting, error, data, confirmationChecked, files]);

  useEffect(() => {
    if (!authed) {
      setLoading(false);
      return;
    }

    if (!token) {
      setError("Invalid boot install link");
      setLoading(false);
      return;
    }

    fetchBootInstallData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authed]);

  const fetchBootInstallData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${REACT_APP_API_URL}boot-install/${token}`, {
        headers: {
          "api-key": REACT_APP_API_KEY,
          ...officerAuthHeader(),
        },
      });

      if (response.status === 200 && response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || "Failed to load boot install data");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        officerLogout();
        setAuthed(false);
        toast.error("Your session has expired. Please sign in again.");
      } else if (err.response?.status === 410) {
        setError("This boot install link has expired");
      } else if (err.response?.status === 404) {
        setError("Invalid boot install link");
      } else {
        setError(err.response?.data?.message || "Failed to load boot install data");
      }
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      setFiles(selected.slice(0, maxFiles));
      return;
    }
    setFiles(selected);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!confirmationChecked) {
      toast.error("Please confirm the boot has been installed");
      return;
    }

    if (!files || files.length < 1) {
      toast.error("Please upload at least one boot photo");
      return;
    }

    if (files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const sentToken = getOfficerToken();
    const cancelSource = axios.CancelToken.source();
    submitCancelRef.current = cancelSource;

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("token", token);
      formData.append("confirmation_checked", "1");
      files.forEach((f) => formData.append("boot_images[]", f));

      const response = await axios.post(`${REACT_APP_API_URL}boot-install/submit`, formData, {
        cancelToken: cancelSource.token,
        headers: {
          "api-key": REACT_APP_API_KEY,
          "Content-Type": "multipart/form-data",
          ...officerAuthHeader(),
        },
      });

      // Ignore a response only when this tab abandoned the submit.
      // A token change in another tab is not an abandon: the request
      // already carries this officer, and the server may already have committed it.
      if (submitCancelRef.current !== cancelSource) {
        return;
      }

      if (response.status === 200 && response.data?.success) {
        toast.success(response.data?.message || "Boot installation confirmed");
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get("returnTo");
        if (returnTo) {
          history.push(returnTo);
        } else {
          history.push(routes.home.path);
        }
        return;
      }

      toast.error(response.data?.message || "Failed to submit boot installation");
    } catch (err) {
      // Axios 0.21 rejects xhr.abort() as ECONNABORTED, not axios.Cancel.
      if (axios.isCancel(err) || submitCancelRef.current !== cancelSource) {
        return;
      }
      if (err.response?.status === 401) {
        // localStorage is shared. A 401 for the officer who sent this
        // request must not delete a newer token written by another tab.
        if (getOfficerToken() !== sentToken) {
          setOfficerName(getOfficerName() || "");
          toast.error(err.response?.data?.message || "Failed to submit boot installation");
        } else {
          officerLogout();
          setAuthed(false);
          toast.error("Your session has expired. Please sign in again.");
        }
      } else {
        toast.error(err.response?.data?.message || "Failed to submit boot installation");
      }
    } finally {
      if (submitCancelRef.current === cancelSource) {
        submitCancelRef.current = null;
        setSubmitting(false);
      }
    }
  };

  const signInAsSomeoneElse = () => {
    const pendingSubmit = submitCancelRef.current;
    submitCancelRef.current = null;
    officerLogout();
    if (pendingSubmit) {
      pendingSubmit.cancel();
    }
    setOfficerName("");
    setAuthed(false);
    setData(null);
    setConfirmationChecked(false);
    setFiles([]);
    setError(null);
    setSubmitting(false);
  };

  if (!authed) {
    return (
      <section className="main_section" style={{ backgroundColor: "#ffffff", color: "#000000" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h3 style={{ marginBottom: "15px" }}>Boot Installation Confirmation</h3>
              <OfficerLoginForm
                onSuccess={() => {
                  setOfficerName(getOfficerName() || "");
                  setAuthed(true);
                }}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loading && !data) {
    return (
      <section className="main_section" style={{ backgroundColor: "#ffffff", color: "#000000" }}>
        <ApiLoader />
      </section>
    );
  }

  return (
    <section className="main_section" style={{ backgroundColor: "#ffffff", color: "#000000" }}>
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            {loading && <ApiLoader />}

            <h3 style={{ marginBottom: "15px" }}>Boot Installation Confirmation</h3>
            <p style={{ marginBottom: "15px" }}>
              Signed in as <strong>{officerName || "the account saved on this device"}</strong>.
              This install is credited to that account.
              {" "}
              <button
                type="button"
                className="btn btn-link"
                style={{ padding: 0 }}
                onClick={signInAsSomeoneElse}
                disabled={submitting}
              >
                Sign in as someone else
              </button>
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {!error && data && (
              <>
                <div className="panel panel-default" style={{ marginBottom: "15px" }}>
                  <div className="panel-body">
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Violation ID:</strong> {data.violation?.id}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Plate:</strong> {data.violation?.license_plate_number || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Boot Serial:</strong> {data.violation?.boot_serial_number || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>VIN:</strong> {data.violation?.vin_number || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Make / Model:</strong> {`${data.violation?.make || ""} ${data.violation?.model || ""}`.trim()}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Color:</strong> {data.violation?.color || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Reason:</strong> {data.violation?.reason || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Fine Amount:</strong> {data.violation?.fine_amount || ""}
                    </p>
                    <p style={{ marginBottom: "6px" }}>
                      <strong>Property:</strong> {data.property?.name || ""}
                    </p>
                    <p style={{ marginBottom: "0px" }}>
                      <strong>Address:</strong> {data.property?.address || ""}
                    </p>
                  </div>
                </div>

                {Array.isArray(data.images) && data.images.length > 0 && (
                  <div className="panel panel-default" style={{ marginBottom: "15px" }}>
                    <div className="panel-body">
                      <h4 style={{ marginTop: 0, marginBottom: "10px", color: "#000000" }}>Existing Violation Images</h4>
                      <div className="row">
                        {data.images
                          .filter((img) => img && img.url)
                          .map((img) => (
                            <div key={img.id} className="col-sm-4" style={{ marginBottom: "12px" }}>
                              <a href={img.url} target="_blank" rel="noreferrer">
                                <img
                                  src={img.url}
                                  alt={`violation-${img.id}`}
                                  style={{ width: "100%", border: "1px solid #ddd" }}
                                />
                              </a>
                              <div style={{ fontSize: "12px", marginTop: "6px", color: "#000000" }}>
                                <div>
                                  <strong>Type:</strong> {img.image_type}
                                </div>
                                {img.taken_at && (
                                  <div>
                                    <strong>Taken:</strong> {img.taken_at}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                {data.already_booted ? (
                  <div className="alert alert-success" role="alert">
                    This violation already has boot photos uploaded.
                  </div>
                ) : (
                  <form onSubmit={submit}>
                    <div className="checkbox" style={{ marginBottom: "10px" }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={confirmationChecked}
                          onChange={(e) => setConfirmationChecked(e.target.checked)}
                        />
                        &nbsp;I confirm the boot has been installed on the vehicle.
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Upload boot photos (1 to {maxFiles})</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        multiple
                        onChange={onFileChange}
                        disabled={submitting}
                      />
                      {files.length > 0 && (
                        <small className="help-block">Selected: {files.length} file(s)</small>
                      )}
                    </div>

                    <button type="submit" className="btn btn-warning" disabled={!canSubmit}>
                      {submitting ? "Submitting..." : "Submit Confirmation"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BootInstall;
